/**
 * Returns whether the user has any unclaimed offers available right now —
 * drives the pulsing accent dot on the Offers tab in BottomNav so users in
 * other tabs notice when there's something to claim.
 *
 * hasOffers = true when any of:
 *   - the active branch has at least one Today's Special, OR
 *   - the logged-in user can claim a free coffee right now (Business Rule #5), OR
 *   - they hold an unclaimed app-install welcome coffee (Business Rule #45).
 *
 * Cache: module-level Map keyed by `${token}:${branchId}`, 60s TTL. Tab
 * switches re-mount BottomNav but reuse the cache so we don't hammer the API.
 * Refetches on auth or branch change (cacheKey shifts → effect re-fires).
 * Errors degrade silently to hasOffers=false.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_TTL_MS = 60_000;
const cache = new Map();

function readBranchId() {
  try {
    const stored = localStorage.getItem('selectedBranch');
    return stored ? JSON.parse(stored)?.id || null : null;
  } catch {
    return null;
  }
}

/**
 * @returns {{ hasOffers: boolean, loading: boolean }}
 */
export function useOffersAvailability() {
  const { token } = useAuth();
  const branchId = readBranchId();
  const cacheKey = `${token || 'anon'}:${branchId || 'none'}`;

  const [state, setState] = useState(() => {
    const entry = cache.get(cacheKey);
    if (entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS) {
      return { hasOffers: entry.data.hasOffers, loading: false };
    }
    return { hasOffers: false, loading: true };
  });

  useEffect(() => {
    const entry = cache.get(cacheKey);
    if (entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS) {
      setState({ hasOffers: entry.data.hasOffers, loading: false });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    const branchQuery = branchId ? `?branch_id=${branchId}` : '';
    const offersPromise = axios
      .get(`${API}/api/offers-page/offer-of-the-day${branchQuery}`)
      .catch(() => ({ data: [] }));
    const freeCoffeePromise = token
      ? axios
          .get(`${API}/api/free-coffee/progress${branchQuery}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: null }))
      : Promise.resolve({ data: null });

    Promise.all([offersPromise, freeCoffeePromise]).then(([offersRes, freeRes]) => {
      if (cancelled) return;
      const offers = Array.isArray(offersRes.data) ? offersRes.data : [];
      const free = freeRes.data;
      // An unclaimed welcome coffee (Business Rule #45) is exactly the kind of
      // "something is waiting for you" the dot exists to signal — and it's the
      // first thing a brand-new installer has, before any loyalty progress.
      const welcomeWaiting = free?.welcome?.active && free?.welcome?.status === 'granted';
      const result = {
        hasOffers: offers.length > 0
          || Boolean(free?.enabled && free?.can_claim)
          || Boolean(welcomeWaiting),
      };
      cache.set(cacheKey, { data: result, fetchedAt: Date.now() });
      setState({ hasOffers: result.hasOffers, loading: false });
    });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, token, branchId]);

  return state;
}
